import React, { useState, useMemo } from 'react';
import {
  Briefcase,
  AlertTriangle,
  Search,
  RefreshCw,
  DollarSign,
  Calendar,
  Building2,
  User,
  Link,
  ExternalLink,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

// Sort types
type SortField = 'createdAt' | 'name' | 'closeDate' | 'totalRevenue' | 'salesRepName' | 'referralSourceName';
type SortOrder = 'asc' | 'desc';
import { useBusiness } from '@/contexts/BusinessContext';
import { useAuth } from '@/contexts/AuthContext';
import { useMyProjects, useProjects, useLinkReferralSource } from '@/hooks/useProjects';
import { useCompanies } from '@/hooks/useCompanies';
import { useSalesReps } from '@/hooks/useUsers';
import { formatCurrency } from '@/services/projects';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { projectsPageTour } from '@/walkthrough/tourSteps';
import type { Project, Company } from '@/types';

export function ProjectsPage() {
  const { currentBusiness, getBusinessLabel } = useBusiness();
  const { isOwnerOrAdmin } = useAuth();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(projectsPageTour);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [closeDateFilter, setCloseDateFilter] = useState<string>('all');
  const [salesRepFilter, setSalesRepFilter] = useState<string>('all');
  const [referralSourceFilter, setReferralSourceFilter] = useState<string>('all');
  const [referralLinkedFilter, setReferralLinkedFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [linkingProjectId, setLinkingProjectId] = useState<string | null>(null);
  // Sort state - default to newest first (createdAt descending)
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Use different query based on role
  const { data: projects, isLoading, error, refetch } = isOwnerOrAdmin
    ? useProjects()
    : useMyProjects();

  const { data: companies } = useCompanies();
  const { data: salesReps } = useSalesReps();
  const linkReferralMutation = useLinkReferralSource();

  // Extract unique project types from projects
  const projectTypes = useMemo(() => {
    if (!projects) return [];
    const types = new Set<string>();
    projects.forEach((p) => {
      if (p.projectType) types.add(p.projectType);
    });
    return Array.from(types).sort();
  }, [projects]);

  // Extract unique referral sources (companies that have been linked)
  const linkedReferralSources = useMemo(() => {
    if (!projects) return [];
    const sources = new Map<string, string>();
    projects.forEach((p) => {
      if (p.referralSourceId && p.referralSourceName) {
        sources.set(p.referralSourceId, p.referralSourceName);
      }
    });
    return Array.from(sources.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Generate close date options (months with data)
  const closeDateOptions = useMemo(() => {
    if (!projects) return [];
    const months = new Map<string, string>();
    projects.forEach((p) => {
      if (p.closeDate) {
        const date = new Date(p.closeDate);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        months.set(key, label);
      }
    });
    return Array.from(months.entries())
      .sort((a, b) => b[0].localeCompare(a[0])) // Sort descending (newest first)
      .map(([value, label]) => ({ value, label }));
  }, [projects]);

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    // First filter
    const filtered = projects.filter((project) => {
      // Search filter
      const matchesSearch =
        !searchQuery ||
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.referralSourceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.salesRepName?.toLowerCase().includes(searchQuery.toLowerCase());

      // Commission status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'paid' && project.commissionPaid) ||
        (statusFilter === 'unpaid' && !project.commissionPaid);

      // Type filter
      if (typeFilter !== 'all' && project.projectType !== typeFilter) {
        return false;
      }

      // Sales Rep filter (for admins viewing all projects)
      if (salesRepFilter !== 'all' && project.salesRepUserId !== salesRepFilter) {
        return false;
      }

      // Referral Source filter
      if (referralSourceFilter !== 'all' && project.referralSourceId !== referralSourceFilter) {
        return false;
      }

      // Referral Linked filter
      if (referralLinkedFilter === 'linked' && !project.referralSourceId) {
        return false;
      }
      if (referralLinkedFilter === 'unlinked' && project.referralSourceId) {
        return false;
      }

      // Close Date filter (by month)
      if (closeDateFilter !== 'all' && project.closeDate) {
        const date = new Date(project.closeDate);
        const projectMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (projectMonth !== closeDateFilter) {
          return false;
        }
      } else if (closeDateFilter !== 'all' && !project.closeDate) {
        return false;
      }

      return matchesSearch && matchesStatus;
    });

    // Then sort
    return [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'closeDate':
          aValue = a.closeDate ? new Date(a.closeDate).getTime() : 0;
          bValue = b.closeDate ? new Date(b.closeDate).getTime() : 0;
          break;
        case 'totalRevenue':
          aValue = a.totalRevenue || 0;
          bValue = b.totalRevenue || 0;
          break;
        case 'salesRepName':
          aValue = a.salesRepName?.toLowerCase() || '';
          bValue = b.salesRepName?.toLowerCase() || '';
          break;
        case 'referralSourceName':
          aValue = a.referralSourceName?.toLowerCase() || '';
          bValue = b.referralSourceName?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      // Handle null/undefined - push to end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare
      let comparison = 0;
      if (typeof aValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = aValue - bValue;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [projects, searchQuery, statusFilter, typeFilter, salesRepFilter, referralSourceFilter, referralLinkedFilter, closeDateFilter, sortField, sortOrder]);

  // Handle sort change
  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Default to desc for dates/numbers, asc for text
      const numericFields: SortField[] = ['createdAt', 'closeDate', 'totalRevenue'];
      setSortOrder(numericFields.includes(field) ? 'desc' : 'asc');
    }
  };

  const handleLinkReferral = async (projectId: string, company: Company) => {
    await linkReferralMutation.mutateAsync({
      projectId,
      referralSourceId: company.id,
      referralSourceName: company.name,
    });
    setLinkingProjectId(null);
  };

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">Failed to load projects</p>
          <button onClick={() => refetch()} className="btn btn-primary">
            <RefreshCw size={20} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const hasProjects = projects && projects.length > 0;

  return (
    <div className="space-y-6">
      <div data-tour="projects-header" className="page-header">
        <h1 className="page-title">
          {isOwnerOrAdmin ? 'All Projects' : 'My Projects'}
        </h1>
        <p className="page-description">
          {isOwnerOrAdmin
            ? `View all projects for ${getBusinessLabel(currentBusiness)}. Projects are synced from the Portal.`
            : `View your assigned projects for ${getBusinessLabel(currentBusiness)}. Projects are synced from the Portal.`}
        </p>
      </div>

      {/* Configuration warning - only show if no projects */}
      {!isLoading && !hasProjects && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-yellow-800">No Projects Synced</p>
            <p className="text-sm text-yellow-700 mt-1">
              Projects will appear here once they are synced from the Portal.
              Please contact your admin if you expected to see projects here.
            </p>
          </div>
        </div>
      )}

      {/* Search and filters */}
      {hasProjects && (
        <div data-tour="projects-filters" className="card space-y-4">
          {/* Row 1: Search and Commission Status */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by project number, customer name, referral source, or rep..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="input sm:w-48"
            >
              <option value="all">All Commission Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>

          {/* Row 2: Type, Close Date, Sales Rep, Referral Source */}
          <div className="flex flex-wrap gap-4">
            {/* Type Filter */}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600 whitespace-nowrap">Type:</span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="input w-auto"
              >
                <option value="all">All</option>
                {projectTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Close Date Filter */}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600 whitespace-nowrap">Close Date:</span>
              <select
                value={closeDateFilter}
                onChange={(e) => setCloseDateFilter(e.target.value)}
                className="input w-auto"
              >
                <option value="all">All</option>
                {closeDateOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sales Rep Filter - only show for admins */}
            {isOwnerOrAdmin && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-600 whitespace-nowrap">Sales Rep:</span>
                <select
                  value={salesRepFilter}
                  onChange={(e) => setSalesRepFilter(e.target.value)}
                  className="input w-auto"
                >
                  <option value="all">All</option>
                  {salesReps?.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.displayName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Referral Source Filter */}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600 whitespace-nowrap">Referral Source:</span>
              <select
                value={referralSourceFilter}
                onChange={(e) => setReferralSourceFilter(e.target.value)}
                className="input w-auto"
              >
                <option value="all">All</option>
                {linkedReferralSources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Referral Linked Filter */}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600 whitespace-nowrap">Referral Linked:</span>
              <select
                value={referralLinkedFilter}
                onChange={(e) => setReferralLinkedFilter(e.target.value as typeof referralLinkedFilter)}
                className="input w-auto"
              >
                <option value="all">All</option>
                <option value="linked">Linked</option>
                <option value="unlinked">Unlinked</option>
              </select>
            </div>

            {/* Sort Control */}
            <div className="flex gap-2 items-center ml-auto">
              <span className="text-sm text-gray-600 whitespace-nowrap">Sort by:</span>
              <select
                value={sortField}
                onChange={(e) => handleSortChange(e.target.value as SortField)}
                className="input w-auto"
              >
                <option value="createdAt">Date Added</option>
                <option value="name">Name</option>
                <option value="closeDate">Close Date</option>
                <option value="totalRevenue">Revenue</option>
                <option value="salesRepName">Sales Rep</option>
                <option value="referralSourceName">Referral Source</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? (
                  <ChevronUp size={18} className="text-blue-600" />
                ) : (
                  <ChevronDown size={18} className="text-blue-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Projects list */}
      {isLoading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        </div>
      ) : filteredProjects.length > 0 ? (
        <div data-tour="project-cards" className="space-y-4">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              companies={companies || []}
              isLinking={linkingProjectId === project.id}
              onStartLink={() => setLinkingProjectId(project.id)}
              onCancelLink={() => setLinkingProjectId(null)}
              onLink={(company) => handleLinkReferral(project.id, company)}
            />
          ))}
        </div>
      ) : hasProjects ? (
        <div className="card">
          <div className="empty-state">
            <Search size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No matching projects</p>
            <p className="mt-1">Try adjusting your search or filters.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Briefcase size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No projects yet</p>
            <p className="mt-1">
              Your assigned projects will appear here once synced from the Portal.
            </p>
          </div>
        </div>
      )}

      {/* Stats footer */}
      {hasProjects && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {filteredProjects.length} of {projects?.length || 0} projects
          </span>
          <button
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setTypeFilter('all');
              setCloseDateFilter('all');
              setSalesRepFilter('all');
              setReferralSourceFilter('all');
              setReferralLinkedFilter('all');
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            Clear filters
          </button>
        </div>
      )}

      <WelcomeModal
        pageName={projectsPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}

// ===========================================
// Project Card Component
// ===========================================

interface ProjectCardProps {
  project: Project;
  companies: Company[];
  isLinking: boolean;
  onStartLink: () => void;
  onCancelLink: () => void;
  onLink: (company: Company) => void;
}

function ProjectCard({
  project,
  companies,
  isLinking,
  onStartLink,
  onCancelLink,
  onLink,
}: ProjectCardProps) {
  const [companySearch, setCompanySearch] = useState('');

  const filteredCompanies = useMemo(() => {
    if (!companySearch) return companies.slice(0, 5);
    return companies
      .filter((c) => c.name.toLowerCase().includes(companySearch.toLowerCase()))
      .slice(0, 5);
  }, [companies, companySearch]);

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* Main info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{project.name}</h3>
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
              {project.projectType}
            </span>
          </div>
          {project.customerName && (
            <p className="text-sm text-gray-500 mt-0.5">{project.customerName}</p>
          )}

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-600">
            {project.createdAt && (
              <span className="flex items-center gap-1 text-gray-500">
                <Calendar size={14} />
                Added {new Date(project.createdAt).toLocaleDateString()}
              </span>
            )}
            {project.salesRepName && (
              <span className="flex items-center gap-1">
                <User size={14} />
                {project.salesRepName}
              </span>
            )}
            {project.closeDate && (
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                Closed {project.closeDate.toLocaleDateString()}
              </span>
            )}
            {project.referralSourceName ? (
              <span className="flex items-center gap-1 text-blue-600">
                <Building2 size={14} />
                {project.referralSourceName}
                {project.referralContactName && (
                  <span className="text-gray-500 font-normal"> ({project.referralContactName})</span>
                )}
              </span>
            ) : (
              <button
                onClick={onStartLink}
                data-tour="link-referral"
                className="flex items-center gap-1 text-gray-400 hover:text-blue-600"
              >
                <Link size={14} />
                Link referral source
              </button>
            )}
          </div>
        </div>

        {/* Financial info */}
        <div className="flex flex-col sm:items-end gap-1">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-gray-900">
              {formatCurrency(project.totalRevenue)}
            </span>
            <span className="text-sm text-gray-500">revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`font-medium ${
                project.commissionPaid ? 'text-green-600' : 'text-orange-600'
              }`}
            >
              {formatCurrency(project.expectedCommission)}
            </span>
            <span
              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                project.commissionPaid
                  ? 'bg-green-100 text-green-800'
                  : 'bg-orange-100 text-orange-800'
              }`}
            >
              {project.commissionPaid ? 'Paid' : 'Unpaid'}
            </span>
          </div>
        </div>
      </div>

      {/* Link referral source UI */}
      {isLinking && (
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-gray-700">
              Select referral source:
            </span>
            <button
              onClick={onCancelLink}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
          <input
            type="text"
            placeholder="Search companies..."
            value={companySearch}
            onChange={(e) => setCompanySearch(e.target.value)}
            className="input mb-2"
            autoFocus
          />
          <div className="space-y-1">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => onLink(company)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 flex items-center gap-2"
                >
                  <Building2 size={16} className="text-gray-400" />
                  <span>{company.name}</span>
                  {company.type && (
                    <span className="text-xs text-gray-500">({company.type})</span>
                  )}
                </button>
              ))
            ) : (
              <p className="text-sm text-gray-500 py-2">
                No companies found. Create one first.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
